import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import markDroppable from 'editor/modifiers/mark-droppable';

interface DragDropContainerSignature {
  Element: HTMLDivElement;
  Args: {
    // eslint-disable-next-line no-unused-vars
    onDrop: (files: FileList) => void;
    isActive?: boolean;
  };
  Blocks: {
    default: [];
  };
}

export default class DragDropContainer extends Component<DragDropContainerSignature> {
  constructor(owner: unknown, args: DragDropContainerSignature['Args']) {
    super(owner, args);
  }

  @tracked isDragging = false;
  dragTarget?: EventTarget | null;

  isActive() {
    return this.args.isActive ?? true;
  }

  @action
  handleDragEnter(ev: DragEvent) {
    this.#cancelEvents(ev);
    if (!this.isActive()) {
      return;
    }
    this.isDragging = true;
    this.dragTarget = ev.target;
  }

  @action
  handleDragOver(ev: DragEvent) {
    this.#cancelEvents(ev);
    if (ev.dataTransfer) {
      ev.dataTransfer.dropEffect = 'copy';
    }
  }

  @action
  handleDragLeave(ev: DragEvent) {
    if (ev.target == this.dragTarget) {
      this.#cancelEvents(ev);
      this.isDragging = false;
      this.dragTarget = undefined;
    }
  }

  @action
  async handleDrop(ev: DragEvent) {
    if (!this.isActive()) {
      return;
    }
    let files = ev.dataTransfer?.files;
    this.isDragging = false;
    this.dragTarget = undefined;
    if (files?.length) {
      this.#cancelEvents(ev);
      this.args.onDrop(files);
    }
  }

  #cancelEvents(ev: DragEvent) {
    ev.stopPropagation();
    ev.preventDefault();
  }

  <template>
    <div
      ...attributes
      {{markDroppable this.isDragging}}
      {{on "dragenter" this.handleDragEnter}}
      {{on "dragover" this.handleDragOver}}
      {{on "dragleave" this.handleDragLeave}}
      {{on "drop" this.handleDrop}}
    >
      {{yield}}
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    DragDropContainer: typeof DragDropContainer;
  }
}
